import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Shield, Ban, UserCheck, Search, Trash2, Users, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

const Moderation = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [moderators, setModerators] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.role === 'super_admin';
  const canModerate = ['super_admin', 'admin', 'moderator'].includes(user?.role);

  useEffect(() => {
    if (canModerate) {
      loadData();
    }
  }, [canModerate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, bannedRes] = await Promise.all([
        api.get('/moderation/users'),
        api.get('/moderation/banned-users')
      ]);
      setUsers(usersRes.data);
      setBannedUsers(bannedRes.data);

      if (isSuperAdmin) {
        const modRes = await api.get('/moderation/moderators');
        setModerators(modRes.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    try {
      const res = await api.get(`/moderation/users?search=${searchTerm}`);
      setUsers(res.data);
    } catch (error) {
      toast.error('Errore nella ricerca');
    }
  };

  const banUser = async (userId, gamertag) => {
    if (!window.confirm(`Sei sicuro di voler bannare ${gamertag}?`)) return;
    try {
      await api.post(`/moderation/ban-user/${userId}`);
      toast.success(`${gamertag} bannato dalla chat`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const unbanUser = async (userId, gamertag) => {
    try {
      await api.post(`/moderation/unban-user/${userId}`);
      toast.success(`${gamertag} sbannato`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const setModerator = async (userId, gamertag) => {
    if (!window.confirm(`Promuovere ${gamertag} a Moderatore?`)) return;
    try {
      await api.post(`/moderation/set-moderator/${userId}`);
      toast.success(`${gamertag} è ora Moderatore`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const removeModerator = async (userId, gamertag) => {
    if (!window.confirm(`Rimuovere ${gamertag} dai Moderatori?`)) return;
    try {
      await api.post(`/moderation/remove-moderator/${userId}`);
      toast.success(`${gamertag} non è più Moderatore`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: 'Super Admin', color: 'bg-red-500' },
      admin: { label: 'Admin', color: 'bg-purple-500' },
      moderator: { label: 'Moderatore', color: 'bg-blue-500' },
      captain: { label: 'Capitano', color: 'bg-yellow-500' },
      player: { label: 'Giocatore', color: 'bg-gray-500' }
    };
    const badge = badges[role] || badges.player;
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (!canModerate) {
    return (
      <div className="min-h-screen ept-bg-ambient text-white flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold">Accesso Negato</h2>
          <p className="text-slate-400 mt-2">Solo i moderatori possono accedere a questa pagina</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ept-bg-ambient text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Shield className="w-10 h-10 text-blue-400" />
          <div>
            <h1 className="text-3xl font-bold">Pannello Moderazione</h1>
            <p className="text-slate-400">Gestisci utenti e modera la chat</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? 'bg-blue-600' : 'bg-[#1a1f2e]'}
          >
            <Users className="w-4 h-4 mr-2" />
            Utenti
          </Button>
          <Button
            onClick={() => setActiveTab('banned')}
            className={activeTab === 'banned' ? 'bg-red-600' : 'bg-[#1a1f2e]'}
          >
            <Ban className="w-4 h-4 mr-2" />
            Bannati ({bannedUsers.length})
          </Button>
          {isSuperAdmin && (
            <Button
              onClick={() => setActiveTab('moderators')}
              className={activeTab === 'moderators' ? 'bg-purple-600' : 'bg-[#1a1f2e]'}
            >
              <Shield className="w-4 h-4 mr-2" />
              Moderatori ({moderators.length})
            </Button>
          )}
        </div>

        {/* Search */}
        {activeTab === 'users' && (
          <div className="flex gap-3 mb-6">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca per gamertag, email o nome..."
              className="flex-1 bg-[#1a1f2e] border-[#2a3142]"
              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
            />
            <Button onClick={searchUsers} className="bg-blue-600">
              <Search className="w-5 h-5" />
            </Button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-[#1a1f2e] rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#050508]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Gamertag</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Ruolo</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Stato</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-400">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-t border-[#2a3142] hover:bg-[#252a3a]">
                        <td className="px-4 py-3 font-medium">{u.gamertag || u.name}</td>
                        <td className="px-4 py-3 text-slate-400 text-sm">{u.email}</td>
                        <td className="px-4 py-3">{getRoleBadge(u.role)}</td>
                        <td className="px-4 py-3">
                          {u.is_banned ? (
                            <span className="text-red-400 text-sm">Bannato</span>
                          ) : (
                            <span className="text-green-400 text-sm">Attivo</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-2 justify-end">
                            {!['super_admin', 'admin', 'moderator'].includes(u.role) && (
                              <>
                                {u.is_banned ? (
                                  <Button
                                    size="sm"
                                    onClick={() => unbanUser(u.user_id, u.gamertag || u.name)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <UserCheck className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => banUser(u.user_id, u.gamertag || u.name)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                                {isSuperAdmin && !u.is_banned && (
                                  <Button
                                    size="sm"
                                    onClick={() => setModerator(u.user_id, u.gamertag || u.name)}
                                    className="bg-blue-600 hover:bg-blue-700"
                                  >
                                    <Shield className="w-4 h-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <div className="text-center py-8 text-slate-400">Nessun utente trovato</div>
                )}
              </div>
            )}

            {/* Banned Tab */}
            {activeTab === 'banned' && (
              <div className="bg-[#1a1f2e] rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#050508]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Gamertag</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Email</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-400">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bannedUsers.map((u) => (
                      <tr key={u.user_id} className="border-t border-[#2a3142] hover:bg-[#252a3a]">
                        <td className="px-4 py-3 font-medium text-red-400">{u.gamertag || u.name}</td>
                        <td className="px-4 py-3 text-slate-400 text-sm">{u.email}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            onClick={() => unbanUser(u.user_id, u.gamertag || u.name)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <UserCheck className="w-4 h-4 mr-2" />
                            Sbanna
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {bannedUsers.length === 0 && (
                  <div className="text-center py-8 text-slate-400">Nessun utente bannato</div>
                )}
              </div>
            )}

            {/* Moderators Tab */}
            {activeTab === 'moderators' && isSuperAdmin && (
              <div className="bg-[#1a1f2e] rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#050508]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Gamertag</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Email</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-400">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moderators.map((m) => (
                      <tr key={m.user_id} className="border-t border-[#2a3142] hover:bg-[#252a3a]">
                        <td className="px-4 py-3 font-medium text-blue-400">{m.gamertag || m.name}</td>
                        <td className="px-4 py-3 text-slate-400 text-sm">{m.email}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            onClick={() => removeModerator(m.user_id, m.gamertag || m.name)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Rimuovi
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {moderators.length === 0 && (
                  <div className="text-center py-8 text-slate-400">Nessun moderatore assegnato</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Moderation;
